import type { NaamMatchingResponse } from "@/lib/api/matching-naam";

/**
 * The naam-mode completeness contract — deliberately NOT `inspectMatchingResult`.
 *
 * The birth-mode inspector hard-requires `mangal_dosha` and `person_*_details`,
 * which a name-derived match has by construction NEITHER of. Extending it to
 * tolerate their absence would let a birth-mode result render without a Mangal
 * verdict too. So the two contracts assert opposite things and each rejects the
 * other's shape: this one requires the boundary and `mangal: "not_assessed"`,
 * and fails if a Mangal verdict or lagna is present at all.
 */

const EXPECTED_KOOTAS = new Map([
  ["Varna", 1],
  ["Vashya", 2],
  ["Tara", 3],
  ["Yoni", 4],
  ["Graha Maitri", 5],
  ["Gana", 6],
  ["Bhakoot", 7],
  ["Nadi", 8],
]);

export interface NaamResultQuality {
  complete: boolean;
  reasons: string[];
}

export function inspectNaamMatchingResult(result: NaamMatchingResponse): NaamResultQuality {
  const reasons = [
    ...scoreReasons(result),
    ...kootaReasons(result),
    ...boundaryReasons(result),
    ...fabricationReasons(result),
  ];
  return { complete: reasons.length === 0, reasons };
}

function scoreReasons(result: NaamMatchingResponse): string[] {
  return [
    isBoundedNumber(result.total_score, 0, 36) ? null : "invalid_total_score",
    result.max_score === 36 ? null : "invalid_max_score",
    isBoundedNumber(result.percentage, 0, 100) ? null : "invalid_percentage",
    result.verdict?.trim() ? null : "missing_verdict",
  ].filter(isReason);
}

function kootaReasons(result: NaamMatchingResponse): string[] {
  const byName = new Map((result.kootas ?? []).map((koota) => [koota.name, koota]));
  const itemReasons = [...EXPECTED_KOOTAS].flatMap(([name, max]) => {
    const koota = byName.get(name);
    const slug = name.toLowerCase().replace(/\s+/g, "_");
    if (!koota) return [`missing_koota_${slug}`];
    const valid = koota.max === max && isBoundedNumber(koota.score, 0, max);
    return valid ? [] : [`invalid_koota_${slug}`];
  });
  return [
    ...itemReasons,
    ...(byName.size === EXPECTED_KOOTAS.size ? [] : ["unexpected_koota_count"]),
  ];
}

function boundaryReasons(result: NaamMatchingResponse): string[] {
  // Rule 29 #7: the engine names its own boundary and it travels as DATA. If it
  // is absent the surface has no honest way to render the number at all.
  return [
    result.boundary?.trim() ? null : "missing_boundary",
    result.input_mode === "naam_akshar" ? null : "wrong_input_mode",
    result.recommendation?.trim() ? null : "missing_practical_recommendation",
  ].filter(isReason);
}

function fabricationReasons(result: NaamMatchingResponse): string[] {
  // A Mangal boolean or a lagna here would be data the mode cannot have. Reject
  // it rather than render it: this is the shape a crossed contract arrives in.
  const loose = result as unknown as Record<string, unknown>;
  return [
    result.mangal === "not_assessed" ? null : "mangal_verdict_not_permitted",
    loose.mangal_dosha === undefined ? null : "mangal_verdict_not_permitted",
    loose.person_a_details === undefined && loose.person_b_details === undefined
      ? null
      : "person_details_not_permitted",
  ].filter(isReason);
}

function isReason(reason: string | null): reason is string {
  return reason !== null;
}

function isBoundedNumber(value: number, min: number, max: number): boolean {
  return Number.isFinite(value) && value >= min && value <= max;
}
